// ---------------------------------------------------------------------------
// PawLink · Google Analytics 4 integration
//
// Production-only: this module is a no-op on preview / localhost / any host
// that is not exactly getpawlink.com (or the www subdomain). That way GA4
// never receives traffic from Emergent preview URLs, testing agents, or dev
// machines and the production reports stay clean.
// ---------------------------------------------------------------------------

export const GA4_MEASUREMENT_ID = "G-NKB8CR8L51";

// Hosts on which GA4 must be active. Keep this list tight — every other
// origin (preview, localhost, ip probes) is silently skipped.
const PRODUCTION_HOSTS = new Set(["getpawlink.com", "www.getpawlink.com"]);

let initialized = false;

/** True only when we are running in a real browser AT the production domain. */
export function isProductionHost() {
    if (typeof window === "undefined" || !window.location) return false;
    return PRODUCTION_HOSTS.has(window.location.hostname);
}

/** Thin, safe wrapper around window.gtag that never throws. */
function gtag() {
    if (typeof window === "undefined" || !window.gtag) return;
    try { window.gtag.apply(window, arguments); } catch { /* swallow */ }
}

/**
 * One-time bootstrap. Injects the GA4 gtag script tag, configures the
 * measurement ID, wires an outbound-link auto-tracker, and forwards
 * `beforeinstallprompt` / `appinstalled` window events to GA4.
 *
 * Called from <App />. Safe to call more than once (guarded).
 */
export function initAnalytics() {
    if (initialized) return;
    if (!isProductionHost()) return;
    initialized = true;

    // 1) dataLayer + gtag stub — must be defined before the async script loads.
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    // send_page_view: false — we fire page_view manually on every SPA nav so
    // GA4 gets the correct virtual URL instead of just the entry page.
    window.gtag("config", GA4_MEASUREMENT_ID, {
        send_page_view: false,
        anonymize_ip: true,
    });

    // 2) Inject the async gtag.js loader.
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(s);

    // 3) Global outbound + contact click tracker. One handler fires the right
    // GA4 event based on link protocol/domain. capture:true so it runs before
    // React handlers, and we do NOT preventDefault — GA4 just observes.
    document.addEventListener(
        "click",
        (e) => {
            const a = e.target && e.target.closest && e.target.closest("a[href]");
            if (!a) return;
            const raw = a.getAttribute("href") || "";
            const text = (a.innerText || a.getAttribute("aria-label") || "").trim().slice(0, 100);

            // tel: / sms: / mailto: — dedicated contact events for GA reports.
            if (/^tel:/i.test(raw)) {
                track("contact_click", { method: "call", link_text: text });
                return;
            }
            if (/^sms:/i.test(raw)) {
                track("contact_click", { method: "sms", link_text: text });
                return;
            }
            if (/^mailto:/i.test(raw)) {
                track("contact_click", { method: "email", link_text: text });
                return;
            }
            if (!/^https?:\/\//i.test(raw)) return;

            try {
                const url = new URL(raw, window.location.href);
                // WhatsApp deep-link (wa.me / api.whatsapp.com) is a contact,
                // not a generic outbound — flag it separately.
                if (/(^|\.)wa\.me$/.test(url.hostname) || /(^|\.)whatsapp\.com$/.test(url.hostname)) {
                    track("contact_click", { method: "whatsapp", link_text: text, link_url: url.href });
                    return;
                }
                if (PRODUCTION_HOSTS.has(url.hostname)) return; // same-site — skip
                track("outbound_click", {
                    link_url: url.href,
                    link_domain: url.hostname,
                    link_text: text,
                });
            } catch { /* noop */ }
        },
        { capture: true },
    );

    // 4) PWA install lifecycle → GA4.
    window.addEventListener("beforeinstallprompt", () => {
        track("pwa_prompt_available");
    });
    window.addEventListener("appinstalled", () => {
        track("pwa_installed");
    });
}

/**
 * Report a virtual page_view for the current SPA route.
 * Called by <RouteChangeTracker/> in App.js on every location change.
 */
export function trackPageview(path, title) {
    if (!isProductionHost()) return;
    gtag("event", "page_view", {
        page_path: path || window.location.pathname + window.location.search,
        page_location: window.location.href,
        page_title: title || document.title,
        send_to: GA4_MEASUREMENT_ID,
    });
}

/**
 * Report an arbitrary custom event. `name` should follow GA4 recommended
 * event vocabulary where possible (sign_up, login, begin_checkout, purchase,
 * select_content …) so GA's built-in reports light up.
 */
export function track(name, params) {
    if (!isProductionHost()) return;
    gtag("event", name, { ...(params || {}) });
}
